// frontend/util/aep-current-plans.js
// Guess the client's current plans from the renewal survey (#656 fields:
// current_medicare_coverage + {supp,pdp,mapd}_{carrier,name}) against this
// year's quotes, for the AEP builder's "Current coverage" panel (#721, spec
// §4.1). The survey holds names, not ids; the checkup wrote either the
// carrier-stripped display label or (older saves / free text) the raw catalog
// name, and quotes carry a display `name` plus the raw `original_name`.
// The advisor always verifies the result (spec D2/D3).
import { carrierDisplayName, baseCarrierName, stripCarrierPrefix, applyReplacements, PLAN_NAME_REPLACEMENTS } from '~/util/plan-name'
import { letterFromText, planLetter } from '~/util/aep-recommendation'

export const LINES_BY_COVERAGE = { SUPP_PDP: ['supp', 'pdp'], PDP: ['pdp'], MAPD: ['mapd'] }
const FUZZY_MIN = 0.75

export const normalizeText = s => String(s || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

export const carrierKey = name => normalizeText(baseCarrierName(carrierDisplayName(name || '')))

export function carrierMatches (a, b) {
    const ka = carrierKey(a)
    const kb = carrierKey(b)
    if (!ka || !kb) { return false }
    return ka === kb || ka.startsWith(`${kb} `) || kb.startsWith(`${ka} `)
}

export function planNameKeys (quote) {
    const raw = [quote.name, quote.original_name].filter(Boolean)
    const variants = []
    raw.forEach((n) => {
        variants.push(n, stripCarrierPrefix(n, quote.carrier), applyReplacements(n, PLAN_NAME_REPLACEMENTS), applyReplacements(stripCarrierPrefix(n, quote.carrier), PLAN_NAME_REPLACEMENTS))
    })
    return [...new Set(variants.map(normalizeText).filter(Boolean))]
}

export function jaccard (a, b) {
    const A = new Set(normalizeText(a).split(' ').filter(Boolean))
    const B = new Set(normalizeText(b).split(' ').filter(Boolean))
    if (!A.size || !B.size) { return 0 }
    let inter = 0
    A.forEach((w) => { if (B.has(w)) { inter += 1 } })
    return inter / (A.size + B.size - inter)
}

// Massachusetts standardized plans are named, not lettered.
export function suppLetterFromSurvey (text) {
    const t = String(text || '')
    if (/\bcore\b/i.test(t)) { return 'CORE' }
    if (/\b(?:plan|supplement)\s+1a\b/i.test(t)) { return '1A' }
    if (/\b(?:plan|supplement)\s+1\b/i.test(t)) { return '1' }
    return letterFromText(t)
}

function bestByName (candidates, surveyName) {
    const target = normalizeText(surveyName)
    if (!target) { return null }
    const exact = candidates.find(c => planNameKeys(c).includes(target))
    if (exact) { return { quote: exact, confidence: 'exact' } }
    let best = null
    candidates.forEach((c) => {
        const score = Math.max(...planNameKeys(c).map(k => jaccard(k, target)))
        if (score >= FUZZY_MIN && (!best || score > best.score)) { best = { quote: c, score } }
    })
    return best ? { quote: best.quote, confidence: 'fuzzy' } : null
}

// Among discount variants of one supp: the one whose raw carrier equals the
// survey's, else the Standard variant, else the cheapest.
function pickSuppVariant (candidates, surveyCarrier) {
    const raw = normalizeText(surveyCarrier)
    return candidates.find(c => normalizeText(c.carrier) === raw) ||
        candidates.find(c => /standard\)?$/i.test(String(c.carrier || '').trim()) && !/disc/i.test(c.carrier)) ||
        candidates.slice().sort((a, b) => (parseFloat(a.monthly_premium) || 0) - (parseFloat(b.monthly_premium) || 0))[0]
}

function guessLine (kind, survey, quotes) {
    const carrier = survey[`${kind}_carrier`]
    const name = survey[`${kind}_name`]
    if (!carrier && !name) { return null }
    const type = kind === 'mapd' ? ['mapd', 'ma'] : [kind]
    const byType = quotes.filter(qt => type.includes(qt.plan_type))
    const sameCarrier = carrier ? byType.filter(qt => carrierMatches(carrier, qt.carrier) || carrierMatches(carrier, qt.name)) : byType
    if (!sameCarrier.length) { return null }
    if (kind === 'supp') {
        const letter = suppLetterFromSurvey(name)
        if (letter) {
            const sameLetter = sameCarrier.filter(qt => planLetter(qt) === letter)
            if (sameLetter.length) {
                return { quote: pickSuppVariant(sameLetter, carrier), confidence: 'exact' }
            }
        }
    }
    return bestByName(sameCarrier, name)
}

export function guessCurrentPlans (survey, quotes) {
    const out = { supp: null, pdp: null, mapd: null }
    const kinds = LINES_BY_COVERAGE[survey?.current_medicare_coverage] || []
    if (!kinds.length || !Array.isArray(quotes) || !quotes.length) { return out }
    kinds.forEach((kind) => {
        const hit = guessLine(kind, survey, quotes)
        out[kind] = hit ? { id: hit.quote.id, confidence: hit.confidence, quote: hit.quote } : null
    })
    return out
}
